import { useEffect, useMemo, useState } from "react";
import { format as formatDate } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import type { DateRange, Matcher } from "react-day-picker";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { cn } from "./ui/utils";

/**
 * Two-month, range-only date picker in a popover.
 *
 * The committed value only changes once a pick *completes*, so the trigger
 * keeps showing the previous range while a new one is being chosen — a
 * half-finished range never reaches the filter it drives. Between the two
 * clicks the calendar previews start → hovered day so the span fills in as
 * the cursor moves.
 */
export interface DateRangePickerProps {
  /** Controlled range value. */
  value?: DateRange;
  /** Emits the new range once a pick completes. */
  onChange: (range: DateRange | undefined) => void;
  /** Disable (and strike through) every day after this date. */
  maxDate?: Date;
  /** Disable every day before this date. */
  minDate?: Date;
  placeholder?: string;
  /** `date-fns` token string for the trigger summary. */
  displayFormat?: string;
  /** Months shown side by side. */
  numberOfMonths?: number;
  disabled?: boolean;
  className?: string;
  triggerClassName?: string;
  ariaLabel?: string;
  "data-testid"?: string;
}

const startOfMonth = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), 1);
const addMonths = (d: Date, n: number): Date =>
  new Date(d.getFullYear(), d.getMonth() + n, 1);

/** Order two endpoints so `from` ≤ `to` regardless of pick direction. */
const orderedRange = (a: Date, b: Date): DateRange =>
  b < a ? { from: b, to: a } : { from: a, to: b };

const formatSummary = (range: DateRange | undefined, fmt: string): string => {
  if (!range?.from) return "";
  const from = formatDate(range.from, fmt);
  if (!range.to) return `${from} -`;
  return `${from} - ${formatDate(range.to, fmt)}`;
};

// Overrides on top of the shared Calendar: squared range endpoints and
// struck-out disabled days, which is what distinguishes a range picker from
// the rounded single-date one.
const RANGE_CLASSNAMES = {
  day_range_start:
    "day-range-start !rounded-none aria-selected:bg-indigo-600 aria-selected:text-white",
  day_range_end:
    "day-range-end !rounded-none aria-selected:bg-indigo-600 aria-selected:text-white",
  day_range_middle:
    "!rounded-none aria-selected:bg-indigo-500/15 aria-selected:text-foreground",
  day_disabled: "line-through opacity-40 cursor-not-allowed hover:bg-transparent",
};

export function DateRangePicker({
  value,
  onChange,
  maxDate,
  minDate,
  placeholder = "Date Range",
  displayFormat = "dd/MM/yyyy",
  numberOfMonths = 2,
  disabled = false,
  className,
  triggerClassName,
  ariaLabel,
  "data-testid": testId = "date-range-trigger",
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);

  // `pendingStart` holds the new range's start WHILE a pick is in progress.
  // Reset whenever the popover opens so a previously abandoned pick does not
  // resume half-done.
  const [pendingStart, setPendingStart] = useState<Date | null>(null);
  const [hovered, setHovered] = useState<Date | undefined>(undefined);
  const picking = pendingStart !== null;

  useEffect(() => {
    if (open) {
      setPendingStart(null);
      setHovered(undefined);
    }
  }, [open]);

  const calendarSelected: DateRange | undefined = pendingStart
    ? orderedRange(pendingStart, hovered ?? pendingStart)
    : value;

  const summary = formatSummary(value, displayFormat);

  // Show [previous month, anchor month] so the most relevant month sits on
  // the right. Anchor on the range end, then start, then the cap, else today.
  const leftMonth = useMemo(() => {
    const anchor = value?.to ?? value?.from ?? maxDate ?? new Date();
    return addMonths(startOfMonth(anchor), -(numberOfMonths - 1));
  }, [value?.from, value?.to, maxDate, numberOfMonths]);

  const disabledMatcher: Matcher | undefined =
    maxDate && minDate
      ? { before: minDate, after: maxDate }
      : maxDate
        ? { after: maxDate }
        : minDate
          ? { before: minDate }
          : undefined;

  const handleSelect = (
    _range: DateRange | undefined,
    triggerDate: Date | undefined,
  ) => {
    if (!triggerDate) return;
    if (!pendingStart) {
      setPendingStart(triggerDate);
      return;
    }
    onChange(orderedRange(pendingStart, triggerDate));
    setPendingStart(null);
    setHovered(undefined);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          aria-label={ariaLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          data-testid={testId}
          className={cn(
            // `bg-card`, not `bg-background`: --background is the page surface
            // this control sits ON, so using it painted the trigger the colour
            // of the bar behind it and left it looking recessed next to the
            // dropdowns beside it -- lighter in light theme, darker in dark.
            // Height and radius match MultiSelectDropdown for the same reason:
            // the two are designed to share a filter row.
            "flex h-[38px] w-full items-center gap-2 rounded-lg border border-border bg-card px-3",
            "text-left text-sm text-foreground outline-none transition-colors",
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            "disabled:cursor-not-allowed disabled:opacity-60",
            triggerClassName,
            className,
          )}
        >
          <span className={cn("flex-1 truncate", !summary && "text-muted-foreground")}>
            {summary || placeholder}
          </span>
          <CalendarIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={4} className="w-auto p-3">
        <Calendar
          mode="range"
          numberOfMonths={numberOfMonths}
          defaultMonth={leftMonth}
          toDate={maxDate}
          fromDate={minDate}
          selected={calendarSelected}
          onSelect={handleSelect}
          onDayMouseEnter={(day) => {
            if (picking) setHovered(day);
          }}
          disabled={disabledMatcher}
          showOutsideDays
          classNames={RANGE_CLASSNAMES}
        />
      </PopoverContent>
    </Popover>
  );
}

export type { DateRange };
